import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { ImeiHubServiceId } from '../ImeiHubSignalIcon'
import { useVerifyLoadingProgress } from '../../hooks/useVerifyLoadingProgress'
import { getVerifyLoadingSteps } from '../../utils/verifyLoadingConfig'
import VerifyLoadingScreen from './VerifyLoadingScreen'

type Props = {
  service: ImeiHubServiceId
  serviceTitle: string
  imeiMasked: string
  sessionKey: number
  /** API in flight or success-hold before report reveal */
  loadingActive: boolean
  succeeded: boolean
  failed: boolean
  errorMessage?: string | null
  onReveal: () => void
  onRetry?: () => void
  onDismiss?: () => void
  /** Allow closing modal (demo / error). Active check keeps modal open. */
  dismissible?: boolean
  revealReport: boolean
  savedToProfile?: boolean
  previewDemo?: boolean
  children: ReactNode
}

export default function VerifyFlow({
  service,
  serviceTitle,
  imeiMasked,
  sessionKey,
  loadingActive,
  succeeded,
  failed,
  errorMessage,
  onReveal,
  onRetry,
  onDismiss,
  dismissible = false,
  revealReport,
  savedToProfile = false,
  previewDemo = false,
  children,
}: Props) {
  const { t } = useTranslation()
  const steps = useMemo(() => getVerifyLoadingSteps(service, 'single-request'), [service])
  const revealOnceRef = useRef(false)
  const reportAnchorRef = useRef<HTMLDivElement>(null)

  const scrollToInlineReport = useCallback(() => {
    reportAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const revealReportOnce = useCallback(() => {
    if (revealOnceRef.current) return
    revealOnceRef.current = true
    setLoadingDismissed(true)
    onReveal()
  }, [onReveal])

  const progress = useVerifyLoadingProgress({
    sessionKey,
    active: loadingActive,
    succeeded,
    failed,
    steps,
    onReveal: revealReportOnce,
    autoContinueMs: previewDemo ? 6000 : null,
  })

  const [loadingDismissed, setLoadingDismissed] = useState(false)
  const [reportEntered, setReportEntered] = useState(false)

  useEffect(() => {
    revealOnceRef.current = false
    setLoadingDismissed(false)
    setReportEntered(false)
  }, [sessionKey])

  useEffect(() => {
    if (!revealReport) return
    setLoadingDismissed(true)
    requestAnimationFrame(() => setReportEntered(true))
    let cancelled = false
    const scroll = () => {
      if (!cancelled) scrollToInlineReport()
    }
    const raf1 = requestAnimationFrame(() => requestAnimationFrame(scroll))
    const t1 = window.setTimeout(scroll, 120)
    const t2 = window.setTimeout(scroll, 420)
    return () => {
      cancelled = true
      cancelAnimationFrame(raf1)
      window.clearTimeout(t1)
      window.clearTimeout(t2)
    }
  }, [revealReport, scrollToInlineReport])

  useEffect(() => {
    const dismiss = onDismiss
    if (!dismissible || !dismiss) return
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') dismiss?.()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [dismissible, onDismiss])

  const showLoadingModal =
    !loadingDismissed && (loadingActive || succeeded || failed)
  const showReport = revealReport

  function handleDismiss() {
    onDismiss?.()
  }

  if (!showLoadingModal && !showReport) return null

  const loadingModal =
    showLoadingModal && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="vc-verify-loading-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="verify-loading-modal-title"
          >
            {dismissible && onDismiss ? (
              <button
                type="button"
                className="vc-verify-loading-modal__backdrop"
                aria-label={t('vc.imeiReport.modalClose')}
                onClick={handleDismiss}
              />
            ) : (
              <div className="vc-verify-loading-modal__backdrop vc-verify-loading-modal__backdrop--static" aria-hidden />
            )}
            <div className="vc-verify-loading-modal__panel">
              <div
                className={`verify-flow__layer verify-flow__loading${revealReport ? ' verify-flow__loading--exit' : ''}`}
              >
                <VerifyLoadingScreen
                  variant={service}
                  serviceTitle={serviceTitle}
                  imeiMasked={imeiMasked}
                  steps={steps}
                  stepVisual={progress.stepVisual}
                  statusStepIndex={progress.statusStepIndex}
                  readyMessage={progress.readyMessage}
                  failed={failed}
                  errorMessage={errorMessage}
                  onRetry={onRetry}
                  savedToProfile={savedToProfile}
                  previewDemo={previewDemo}
                  onContinueToReport={previewDemo ? undefined : revealReportOnce}
                  modal
                  titleId="verify-loading-modal-title"
                />
              </div>
            </div>
          </div>,
          document.body,
        )
      : null

  return (
    <>
      {loadingModal}
      {showReport ? (
        <div
          id="vc-imei-verify-report"
          ref={reportAnchorRef}
          className="verify-flow verify-flow--inline-report"
        >
          <div
            className={`verify-flow__layer verify-flow__report${reportEntered ? ' verify-flow__report--enter' : ''}`}
          >
            {children}
          </div>
        </div>
      ) : null}
    </>
  )
}
